import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Avatar,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip
} from '@mui/material';
import {
  People as PeopleIcon,
  Campaign as NotificationsIcon,
  Security as SecurityIcon,
  FamilyRestroom as FamilyIcon,
  Send as SendIcon,
  ManageAccounts as ManageIcon,
  Assessment as ReportIcon,
  Article as LogIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  SwapHoriz as RoleIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs, getCountFromServer, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useProfile } from '../../contexts/ProfileContext';
import { getSystemRoleDisplayName } from '../../utils/systemPermissions';
import { formatDistanceToNow } from 'date-fns';
import logger from '../../utils/logger';

const ACTION_LABELS = {
  user_login: 'User Login',
  user_logout: 'User Logout',
  user_created: 'User Created',
  user_updated: 'User Updated',
  user_deleted: 'User Deleted',
  role_changed: 'Role Changed',
  permission_changed: 'Permission Changed',
  family_created: 'Family Created',
  family_joined: 'Family Joined',
  settings_changed: 'Settings Changed',
  ticket_created: 'Ticket Created',
  ticket_resolved: 'Ticket Resolved',
  admin_action: 'Admin Action'
};

const ACTION_ICONS = {
  user_login: <LoginIcon fontSize="small" />,
  user_logout: <LogoutIcon fontSize="small" />,
  user_created: <PersonAddIcon fontSize="small" />,
  user_updated: <EditIcon fontSize="small" />,
  user_deleted: <DeleteIcon fontSize="small" />,
  role_changed: <RoleIcon fontSize="small" />,
  permission_changed: <SecurityIcon fontSize="small" />,
  family_created: <FamilyIcon fontSize="small" />,
  family_joined: <GroupIcon fontSize="small" />,
  settings_changed: <EditIcon fontSize="small" />,
  admin_action: <AdminIcon fontSize="small" />
};

const ACTION_COLORS = {
  user_deleted: 'error',
  role_changed: 'warning',
  permission_changed: 'warning',
  admin_action: 'warning',
  user_created: 'success',
  family_created: 'success',
  family_joined: 'success',
  user_login: 'default',
  user_logout: 'default'
};

const StatCard = ({ title, value, icon, color, bgColor, loading }) => (
  <Card sx={{ bgcolor: 'grey.800', border: '1px solid rgba(255,255,255,0.12)', height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Avatar sx={{ bgcolor: bgColor, color: color, mr: 2 }}>
          {icon}
        </Avatar>
        <Typography variant="h6">{title}</Typography>
      </Box>
      {loading ? (
        <CircularProgress size={28} sx={{ color }} />
      ) : (
        <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
          {value ?? '—'}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const SystemAdminDashboard = () => {
  const { profile } = useProfile();
  const navigate = useNavigate();

  const [counts, setCounts] = useState({ users: null, families: null, notifications: null, auditRecent: null });
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const sevenDaysAgo = Timestamp.fromDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));

        const results = await Promise.allSettled([
          getCountFromServer(collection(db, 'users')),
          getCountFromServer(collection(db, 'families')),
          getCountFromServer(collection(db, 'systemNotifications')),
          getCountFromServer(
            query(collection(db, 'auditLogs'), where('timestamp', '>=', sevenDaysAgo))
          ),
          getDocs(
            query(collection(db, 'auditLogs'), orderBy('timestamp', 'desc'), limit(10))
          )
        ]);

        const get = (result) => result.status === 'fulfilled' ? result.value : null;

        const usersResult      = get(results[0]);
        const familiesResult   = get(results[1]);
        const notifResult      = get(results[2]);
        const auditCountResult = get(results[3]);
        const activityResult   = get(results[4]);

        setCounts({
          users:         usersResult      ? usersResult.data().count      : null,
          families:      familiesResult   ? familiesResult.data().count   : null,
          notifications: notifResult      ? notifResult.data().count      : null,
          auditRecent:   auditCountResult ? auditCountResult.data().count : null
        });

        if (activityResult) {
          setActivity(activityResult.docs.map(d => ({ id: d.id, ...d.data() })));
        }
      } catch (err) {
        logger.warn('Dashboard stats load error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const stats = [
    {
      title: 'Total Users',
      value: counts.users,
      icon: <PeopleIcon />,
      color: 'primary.main',
      bgColor: 'primary.dark'
    },
    {
      title: 'Active Families',
      value: counts.families,
      icon: <FamilyIcon />,
      color: 'success.main',
      bgColor: 'success.dark'
    },
    {
      title: 'System Notifications',
      value: counts.notifications,
      icon: <NotificationsIcon />,
      color: 'warning.main',
      bgColor: 'warning.dark'
    },
    {
      title: 'Audit Events (7d)',
      value: counts.auditRecent,
      icon: <SecurityIcon />,
      color: 'error.main',
      bgColor: 'error.dark'
    }
  ];

  const quickActions = [
    { label: 'Send System Notification', icon: <SendIcon />,   path: '/system-admin/notifications' },
    { label: 'Manage Users',             icon: <ManageIcon />, path: '/system-admin/users'         },
    { label: 'View Audit Logs',          icon: <LogIcon />,    path: '/system-admin/audit-logs'    },
    { label: 'System Analytics',         icon: <ReportIcon />, path: '/system-admin/analytics'     }
  ];

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    try {
      return formatDistanceToNow(ts.toDate(), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <Box>
      {/* Welcome Section */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.800', borderLeft: 4, borderColor: 'primary.main' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Welcome back, {profile?.firstName || 'Administrator'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Role: {getSystemRoleDisplayName(profile?.role)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {profile?.email}
        </Typography>
        {profile?.familyCode && (
          <Typography variant="body2" color="primary.light" sx={{ mt: 1 }}>
            Also member of family: {profile.familyCode}
          </Typography>
        )}
      </Paper>

      {/* Stats Grid */}
      <Grid container spacing={3}>
        {stats.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.title}>
            <StatCard {...stat} loading={loading} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 0 }}>
        {/* Recent Activity */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, bgcolor: 'grey.800', height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : activity.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No audit events found.
              </Typography>
            ) : (
              <List dense disablePadding>
                {activity.map((event, i) => (
                  <React.Fragment key={event.id}>
                    <ListItem disablePadding sx={{ py: 0.75 }}>
                      <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                        {ACTION_ICONS[event.action] || <LogIcon fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={ACTION_LABELS[event.action] || event.action}
                              size="small"
                              color={ACTION_COLORS[event.action] || 'default'}
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                            <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>
                              {event.targetEmail || event.userEmail || event.performedBy || ''}
                            </Typography>
                          </Box>
                        }
                        secondary={formatTimestamp(event.timestamp)}
                      />
                    </ListItem>
                    {i < activity.length - 1 && <Divider sx={{ opacity: 0.2 }} />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, bgcolor: 'grey.800', height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {quickActions.map(({ label, icon, path }) => (
                <Button
                  key={path}
                  variant="outlined"
                  startIcon={icon}
                  onClick={() => navigate(path)}
                  fullWidth
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  {label}
                </Button>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SystemAdminDashboard;
